/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Box } from '@mantine/core';
import { Suspense } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import type { DedicatedEndpointsProductPlan } from '@fai/friendli-suite/features/products/dedicated-endpoints/plan';
import { useDedicatedEndpointsTeamPlan } from '@fai/friendli-suite/features/products/dedicated-endpoints/plan/use-dedicated-endpoints-team-plan';
import { useDedicatedFreeCredit } from '@fai/friendli-suite/features/products/dedicated-endpoints/use-credit-remaining';
import { usePaymentState } from '@fai/friendli-suite/features/settings/team-settings/use-payment';
import { preventUserAction } from '@fai/ui/f.css';
import Loading from '@fai/ui/views/common/loading';

const getBlockingBoxProps = (
  plan: DedicatedEndpointsProductPlan,
  isPaymentInvalid?: boolean,
  isFreeCreditExist?: boolean
) => {
  const hasPaymentIssue = plan === 'BASIC' && isPaymentInvalid;

  const blockingBoxProps = { className: preventUserAction, opacity: 0.5 };

  return isFreeCreditExist ? {} : hasPaymentIssue ? blockingBoxProps : {};
};

function DEProjectBlockByInvalidPaymentLayout() {
  const { teamId = '' } = useParams();

  const { plan, loading: planLoading } = useDedicatedEndpointsTeamPlan(teamId);
  const { isPaymentInvalid, loading: paymentLoading } = usePaymentState(teamId);
  const { isFreeCreditExist, loading: freeCreditLoading } = useDedicatedFreeCredit(teamId);

  const isLoading = planLoading || paymentLoading || freeCreditLoading;

  if (isLoading) return <Loading />;

  return (
    <Box {...getBlockingBoxProps(plan, isPaymentInvalid, isFreeCreditExist)}>
      <Suspense>
        <Outlet />
      </Suspense>
    </Box>
  );
}

export default DEProjectBlockByInvalidPaymentLayout;
